import React, { useEffect, useState } from 'react';
import { Box, Pagination, Typography, Collapse, Button } from '@mui/material';
import DashboardRow from './DashboardRow';
import { FetchWrapper } from 'components/apiHandling/FetchWrapper';
import APIRequest from 'system/APIRequest';
import { DashboardDocument } from './DashboardDocument';
import i18n from 'translation';

export interface DashboardListProps {
  selectedDashboardDocument: DashboardDocument | undefined;
  setSelectedDashboardDocument: (dashboard: DashboardDocument | undefined) => void;

  currentPage: number;
  setCurrentPage: (page: number) => void;

  // Show Dashboards shared by other users
  viewShared?: boolean;

  // Search String compared against Dashboard Name
  searchString?: string;

  // Search field, most likely "title" or "description"
  searchField?: string;

  // Limit to only Dashboards that are templates
  limitToTemplateDashboards?: boolean;

  // Dashboards in the list must match the given dataset.
  datasetId?: string;
}

export default function DashboardList(props: DashboardListProps): JSX.Element {

  const [DashboardListResponse, setDashboardListResponse] = useState<any>(undefined);
  const [globalDashboardListResponse, setGlobalDashboardListResponse] = useState<any>(undefined);
  const [dashboardsAreLoading, setDashboardsAreLoading] = useState<boolean>(true);
  const [dashboardsError, setQueryError] = useState<any>(undefined);
  const [showMore, setShowMore] = useState(false);

  const totalPages = Math.ceil((DashboardListResponse?.pagination?.total ?? 0) / (DashboardListResponse?.pagination?.per_page ?? 1)) || 0;

  useEffect(() => {
    const requestOptions:any = {
      "sort":"is_favorite,last_viewed_by_you",
      "sort_order":"desc",
      "include_shared": 1
    };

    const searchObj = [{"id":"global_access","op":"=","value":"1"}];
    if (props.limitToTemplateDashboards) {
      searchObj.push({"id":"template_type","op":"not-null","value":""});
    }

    if (props.datasetId) {
      searchObj.push({
        "id": "dataset_id", "op": "contains", "value": props.datasetId
      });
    }

    if (searchObj.length > 0) {
      requestOptions["q"] = JSON.stringify(searchObj);
    }

    APIRequest("obj/dashboard", requestOptions).then((response) => {
      setGlobalDashboardListResponse(response);
      setDashboardsAreLoading(false);
      setQueryError(undefined);
    }).catch((error) => {
      setQueryError(error);
      setGlobalDashboardListResponse(undefined);
      setDashboardsAreLoading(false);
    });
  }, [props.searchString, props.viewShared, props.searchField, props.currentPage]);

  useEffect(() => {
    const searchField = props.searchField || "name";

    const requestOptions:any = {
      "per_page":20,
      "page": props.currentPage,
      "sort":"is_favorite,last_viewed_by_you",
      "sort_order":"desc"
    };
    if (props.viewShared) {
      requestOptions["include_shared"] = 1;
    }
    const searchObj = [];
    if (props.limitToTemplateDashboards) {
      searchObj.push({"id":"template_type","op":"not-null","value":""});
    }
    if (props.searchString) {
      searchObj.push({"id":searchField,"op":"contains","value":props.searchString});
    }
    if (props.datasetId) {
      searchObj.push({
        "id": "dataset_id", "op": "contains", "value": props.datasetId
      });
    }
    if (searchObj.length > 0) {
      requestOptions["q"] = JSON.stringify(searchObj);
    }

    APIRequest("obj/dashboard", requestOptions).then((response) => {
      setDashboardListResponse(response);
      setDashboardsAreLoading(false);
      setQueryError(undefined);
    }).catch((error) => {
      setQueryError(error);
      setDashboardListResponse(undefined);
      setDashboardsAreLoading(false);
    });
  }, [props.searchString, props.viewShared, props.searchField, props.currentPage]);

  // Split global dashboards into first 3 and the rest, for "Show More" functionality
  const globalDashboards = globalDashboardListResponse?.output ?? [];
  const firstGlobalRows = globalDashboards.slice(0, 3).map((dashboard: DashboardDocument, index: number) => (
    <DashboardRow
      key={dashboard.id || index}
      dashboard={dashboard}
      onClick={() => props.setSelectedDashboardDocument(dashboard)}
      selectedDashboardId={props.selectedDashboardDocument?.id}
    />
  ));
  const remainingGlobalRows = globalDashboards.slice(3).map((dashboard: DashboardDocument, index: number) => (
    <DashboardRow
      key={dashboard.id || `more-${index}`}
      dashboard={dashboard}
      onClick={() => props.setSelectedDashboardDocument(dashboard)}
      selectedDashboardId={props.selectedDashboardDocument?.id}
    />
  ));

  const outputRows = DashboardListResponse?.output?.map((dashboard: DashboardDocument, index: number) => {
    return (
      <DashboardRow
        key={index}
        dashboard={dashboard}
        onClick={() => props.setSelectedDashboardDocument(dashboard)}
        selectedDashboardId={props.selectedDashboardDocument?.id}
      />
    );
  });

  return (
    <Box>
      <FetchWrapper isLoading={dashboardsAreLoading} fetchError={dashboardsError} >
        {(!props.viewShared && (!props.searchString || props.searchString?.length < 3) && globalDashboardListResponse?.output?.length > 0) &&
          <>
            <Typography variant="h4" sx={{ mb: 1 }} >
              {i18n.t("widget-types.dashboard-list.global-dashboards")}
            </Typography>
            {firstGlobalRows}
            {remainingGlobalRows.length > 0 && (
              <>
                <Collapse in={showMore}>
                  {remainingGlobalRows}
                </Collapse>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="text"
                    size="small"
                    sx={{ mt: 0, mb: 0.5 }}
                    onClick={() => setShowMore((prev) => !prev)}
                  >
                    {showMore ? i18n.t("view-less") : i18n.t("view-more")}
                  </Button>
                </Box>
              </>
            )}
            {outputRows?.length > 0 &&
              <Typography variant="h4" sx={{ mb: 1 }} >
                {i18n.t("yourdashboards")}
              </Typography>
            }
          </>
        }
        {outputRows}
        <Pagination
          count={totalPages}
          page={props.currentPage}
          onChange={(event: React.ChangeEvent<unknown>, value: number) => {
            props.setCurrentPage(value);
          }}
          shape="rounded"
          sx={{display: 'flex', justifyContent: 'center', pt: 2}}
        />
      </FetchWrapper>
    </Box>
  );
}
